import { Path } from '@/src/types/path';
import { locales } from '@/src/lib/dictionary/settings';
import { SYSTEM_PATHS } from '@/src/lib/utils/constants';
import { SELF_URL } from '@/src/lib/core/config';

export function isAbsoluteUrl(url: string) {
  const regex = new RegExp('^(?:[a-z+]+:)?//', 'i');

  return regex.test(url);
}

function removeTrailingSlash(url: string) {
  return url.replace(/(?:\/+(\?))/, '$1').replace(/\/+$/, '');
}

export function getPureSlug(slug: string) {
  const regex = new RegExp(`/[a-z]+/(${Path.PRODUCT}|${Path.CMS}|${Path.SHOP})?/?`);

  return slug.replace(regex, '');
}

export function isProductPageWithSKU(path: string) {
  const regex = new RegExp('/[a-z]+/p/SKU-[A-Za-z0-9]+');

  return regex.test(path);
}

export function getSKUFromSlug(slug: string) {
  const regex = new RegExp(/(SKU-([A-Za-z0-9\-_]+))/);
  const skuMatch = slug.match(regex);
  if (!skuMatch) return null;
  return skuMatch[2];
}

export function serializeUrlQueryParams(obj: { [key: string]: string }) {
  const str = [];
  for (const p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
    }
  }
  return str.join('&');
}

export function buildPageUrl(
  slug: Array<string>,
  lang: string,
  country: string,
  searchParams?: { [key: string]: string | Array<string> | undefined },
) {
  const pageUrl = new URL(transformURLForFrontendPaths(`/${slug.join('/')}`, lang, country), SELF_URL);
  if (searchParams) {
    Object.keys(searchParams || {}).forEach((key) => {
      if (Array.isArray(searchParams[key])) {
        (searchParams[key] as Array<string>).forEach((value) => {
          pageUrl.searchParams.append(key, value);
        });
        return;
      }
      pageUrl.searchParams.set(key, searchParams[key] as string);
    });
  }
  return pageUrl;
}

export function ensureCorrectPathString(url: string) {
  return url.replace(/^\/?([^/]+(?:\/[^/]+)*)\/?$/, '/$1/') || '/';
}

export function isKnownLanguageInPathname(pathname: string, currentCountry: string) {
  return !locales.every(
    (lang) => !pathname.startsWith(`/${currentCountry}/${lang}/`) && pathname !== `/${currentCountry}/${lang}`,
  );
}

export function isCurrentCountryInPathname(pathname: string, currentCountry: string) {
  return pathname.startsWith(`/${currentCountry}/`) || pathname === `/${currentCountry}`;
}

function isHomepage(url: string, lang: string, country: string): boolean {
  const urlWithoutTrailingSlash = removeTrailingSlash(url);
  return urlWithoutTrailingSlash === transformURLForFrontendPaths('/', lang, country);
}

export function isSystemUrl(url: string, lang: string, country: string): boolean {
  return (
    isHomepage(url, lang, country) ||
    SYSTEM_PATHS.find((path) => url.startsWith(transformURLForFrontendPaths(path, lang, country))) !== undefined
  );
}

export function transformURLForFrontendPaths(url: string, lang: string, country: string): string {
  let newUrl = '';
  if (url.startsWith(`/${lang}/`)) {
    newUrl = url.replace(`/${lang}/`, `/${country.toLowerCase()}/${lang}/shop/`);
  } else {
    newUrl = `/${country.toLowerCase()}/${lang}/shop/${!url.startsWith('/') ? url : url.slice(1)}`;
  }
  return removeTrailingSlash(newUrl);
}

export function transformFrontendURLsForBackend(url: string, lang: string, country: string): string {
  return url.replace(`/${country}/${lang}/shop`, `/${lang}`).replace(/\/$/, '');
}
